import { ROUTE_CUSTOMERS } from '@klear/constants'
import { KlearDate } from '@klear/date'
import type {
  FundCheck,
  FundCheckKey,
  FundCheckOutcome,
  FundReadinessCredit,
  FundReadinessRow,
  FundReadinessStatus
} from '@klear/invoice-offer/fund-readiness'
import { Money } from '@klear/money'

export const FUND_CHECK_LABELS: Record<FundCheckKey, string> = {
  accepted: 'Offer accepted',
  invoice: 'Invoice approved',
  purchaseOrder: 'PO approved',
  proofOfAcceptance: 'POA approved',
  purchasePrice: 'Purchase price',
  paymentDetails: 'Payment details',
  platformFee: 'Platform fee',
  pricing: 'Priced at current credit',
  ehid: 'EHID',
  creditHeadroom: 'Credit headroom'
}

export const DOCUMENT_CHECKS: readonly FundCheckKey[] = ['invoice', 'purchaseOrder', 'proofOfAcceptance']

export function nextInTurn<T>(items: readonly T[], current: T | null, ready: (item: T) => boolean): T | undefined {
  const at = items.findIndex((item) => item === current)
  return [...items.slice(at + 1), ...items.slice(0, Math.max(at, 0))].find(ready)
}

export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

export function daysSince(date: string | null): string | null {
  if (date == null) return null
  const days = Math.max(0, KlearDate.daysBetween(date, KlearDate.today()))
  return days === 0 ? 'today' : `${days}d ago`
}

export function formatDate(iso: string | null | undefined): string {
  return KlearDate.parse(iso ?? undefined)?.toString() ?? '—'
}

export function formatMoney(wire: string | null | undefined): string {
  return wire == null ? '—' : new Money(wire).toString()
}

export type StatusTone = 'ok' | 'warn' | 'info' | 'bad' | 'neutral'

export const STATUS_LABELS: Record<FundReadinessStatus, { label: string; tone: StatusTone }> = {
  ready: { label: 'Ready', tone: 'ok' },
  fundingFailed: { label: 'Funding failed', tone: 'bad' },
  blocked: { label: 'Blocked', tone: 'bad' },
  funding: { label: 'Funding', tone: 'info' },
  quote: { label: 'Quote', tone: 'info' },
  overdue: { label: 'Overdue', tone: 'warn' },
  repaying: { label: 'Repaying', tone: 'info' },
  funded: { label: 'Funded', tone: 'neutral' },
  completed: { label: 'Completed', tone: 'neutral' },
  voided: { label: 'Voided', tone: 'neutral' }
}

export function statusCount(row: FundReadinessRow): number | null {
  return row.blockedFromFunding ? row.fundBlockers.length : null
}

export function fixLink(row: FundReadinessRow, check: FundCheck): { to: string; label: string } | null {
  const { key } = check
  if (check.remedy !== 'editCustomerCredit' || row.customerId == null) return null
  const customer = row.customerName ?? 'the customer'
  return {
    to: `/${ROUTE_CUSTOMERS}/${row.customerId}/edit`,
    label: key === 'ehid' ? `Add EHID on ${customer}` : `Review credit on ${customer}`
  }
}

const OUTCOME_TEXT: Record<'pricing' | 'ehid' | 'creditHeadroom', Record<FundCheckOutcome, string>> = {
  pricing: {
    notNeeded: 'No customer to price',
    passed: "Priced at the customer's current rating",
    notCompared: 'Not compared: no platform fee priced from the rate card'
  },
  ehid: { notNeeded: 'Not needed: investment grade', passed: 'EHID on file', notCompared: 'EHID on file' },
  creditHeadroom: {
    notNeeded: 'Not insured, no limit applies',
    passed: 'Within the available Allianz credit',
    notCompared: 'Not compared: the Allianz limit is in another currency'
  }
}

export function passText(row: FundReadinessRow, check: FundCheck): string {
  switch (check.key) {
    case 'accepted': {
      if (row.fundedAt != null) return `Accepted, then funded ${formatDate(row.fundedAt)}`
      const accepted = daysSince(row.acceptedAt)
      return accepted == null ? 'Accepted' : `Accepted ${accepted}`
    }
    case 'purchasePrice':
      return `${formatMoney(row.purchasePrice)} to send`
    case 'paymentDetails':
      return 'Payment network and accounts set'
    case 'platformFee':
      return 'Platform fee added'
    case 'pricing':
    case 'ehid':
    case 'creditHeadroom':
      return OUTCOME_TEXT[check.key][check.outcome ?? 'passed']
    default:
      return 'Approved'
  }
}

export function creditCell(credit: FundReadinessCredit): { label: string; detail: string } {
  if (credit.classification === 'Insured') {
    return {
      label: 'Insured',
      detail: [credit.rating, credit.allianzLimit ? `limit ${formatMoney(credit.allianzLimit)}` : null]
        .filter(Boolean)
        .join(' · ')
    }
  }
  if (credit.classification === 'IG') return { label: 'Investment grade', detail: credit.rating ?? '' }
  return {
    label: 'Not classified',
    detail: [credit.rating, credit.hasEhid ? null : 'no EHID'].filter(Boolean).join(' · ')
  }
}
